<template>
  <div class="w-full bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
    <div class="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
      <div>
        <h2 class="text-lg font-semibold text-gray-800">Event Summary</h2>
        <p class="text-sm text-gray-500">Calendar events overview</p>
      </div>

      <button class="text-white bg-subheader-line hover:bg-dark-hover px-4 py-2 rounded-lg font-medium text-sm"
        @click="openCreateModal">
        + Create Post
      </button>
    </div>

    <div class="overflow-x-auto">
      <table class="w-full text-sm border-collapse">
        <thead class="bg-gray-50 text-gray-600">
          <tr>
            <th class="px-6 py-3 text-left font-medium">Event Name</th>
            <th class="px-6 py-3 text-left font-medium">Event Description</th>
            <th class="px-6 py-3 text-left font-medium">Date Intended From</th>
            <th class="px-6 py-3 text-left font-medium">Date Intended To</th>
            <th class="px-6 py-3 text-left font-medium">Time Intended</th>
            <th class="px-6 py-3 text-left font-medium">Venue</th>
            <th class="px-6 py-3 text-left font-medium">Date Posted</th>
            <th class="px-6 py-3 text-center font-medium">Action</th>
          </tr>
        </thead>

        <tbody class="divide-y divide-gray-100">
          <tr v-for="event in events" :key="event.id" class="hover:bg-gray-50">
            <td class="px-6 py-4 font-semibold text-gray-800">
              {{ event.event_name }}
            </td>

            <td class="px-6 py-4 text-gray-700">
              {{ event.description }}
            </td>

            <td class="px-6 py-4 text-gray-700">
              {{ formatShortDate(event.date_intended_from) }}
            </td>

            <td class="px-6 py-4 text-gray-700">
              {{ formatShortDate(event.date_intended_to) }}
            </td>

            <td class="px-6 py-4 text-gray-700">
              {{ formatTime(event.time_intended) }}
            </td>

            <td class="px-6 py-4 text-gray-700">
              {{ event.venue }}
            </td>

            <td class="px-6 py-4 text-gray-700">
              {{ formatLongDateTime(event.createdAt) }}
            </td>

            <td class="px-6 py-4 text-center">
              <div class="flex items-center justify-center gap-3">
                <button @click="deleteEvent(event.id)" class="text-red-600 hover:text-red-800 font-medium">
                  Delete
                </button>
              </div>
            </td>
          </tr>

          <tr v-if="events.length === 0 && !isLoading">
            <td colspan="7" class="px-6 py-10 text-center text-gray-400">
              No events found
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <HeroPagination :currentPage="currentPage" :totalPages="totalPages" @page-changed="handlePageChange" />
    <CalendarEventModal v-model="showEventModal" :eventData="selectedEvent" @submit="handleSubmit" />
  </div>
</template>

<script>
import HeroPagination from "@/components/common/HeroPagination.vue";
import CalendarEventModal from "@/components/sections/event-engagement/Modals/CalendarEventModal.vue";
import { ref, onMounted, inject } from "vue";
import {
  formatLongDateTime,
  formatShortDate,
  formatTime,
} from "@/utils/dateFormat";

export default {
  components: {
    HeroPagination,
    CalendarEventModal,
  },
  setup() {
    const axios = inject("$axios");
    const Swal = inject("$swal");
    const events = ref([]);
    const isLoading = ref(true);
    const error = ref(null);
    const currentPage = ref(1);
    const totalPages = ref(1);
    const pageSize = 10;
    const showEventModal = ref(false);
    const selectedEvent = ref(null);

    const fetchEvents = async (page = 1) => {
      try {
        isLoading.value = true;

        const response = await axios.get(
          "/api/module-post-calendar-events/section/3"
        );

        const sortedData = response.data.data.sort((a, b) => b.id - a.id);

        totalPages.value = Math.ceil(sortedData.length / pageSize);

        const start = (page - 1) * pageSize;
        const end = start + pageSize;

        events.value = sortedData.slice(start, end);
      } catch (err) {
        error.value = "Failed to load events.";
        console.error(err);
      } finally {
        isLoading.value = false;
      }
    };

    const handlePageChange = (page) => {
      currentPage.value = page;
      fetchEvents(page);
    };

    onMounted(() => {
      fetchEvents(currentPage.value);
    });

    const openCreateModal = () => {
      selectedEvent.value = null;
      showEventModal.value = true;
    };

    const openEditModal = async (eventId) => {
      try {
        isLoading.value = true;

        const response = await axios.get(
          `/api/module-post-calendar-events/${eventId}`
        );

        selectedEvent.value = response.data.data;
        showEventModal.value = true;
      } catch (err) {
        error.value = "Failed to fetch event.";
        console.error("Failed to fetch event:", err);
      } finally {
        isLoading.value = false;
      }
    };

    const deleteEvent = async (eventId) => {
      try {
        const result = await Swal.fire({
          title: "Are you sure?",
          text: "You won't be able to revert this!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#ef4444",
          cancelButtonColor: "#6b7280",
          confirmButtonText: "Yes, delete it!",
          cancelButtonText: "Cancel",
        });

        if (result.isConfirmed) {
          await axios.delete(`/api/module-post-calendar-events/${eventId}`);

          Swal.fire({
            icon: "success",
            title: "Deleted!",
            text: "Event has been deleted successfully.",
            confirmButtonColor: "#f97316",
          });

          fetchEvents(currentPage.value);
        }
      } catch (err) {
        console.error("Failed to delete event:", err);
        Swal.fire({
          icon: "error",
          title: "Oops!",
          text:
            err.response?.data?.message ||
            "Something went wrong while deleting the event.",
        });
      }
    };

    const handleSubmit = (data) => {
      console.log(data);
      fetchEvents(currentPage.value);
    };

    return {
      events,
      isLoading,
      error,
      fetchEvents,
      formatLongDateTime,
      formatShortDate,
      formatTime,
      currentPage,
      totalPages,
      handlePageChange,
      showEventModal,
      selectedEvent,
      openCreateModal,
      deleteEvent,
      handleSubmit,
      openEditModal,
    };
  },
};
</script>
